<template>
  <div
    ref="cardElement"
    class="model-container"
    :class="{ 'position-relative': !query.isInContext }"
    :style="{
      minHeight: cardHeight,
      marginTop: system.queries[event.index].length === 1 ? '52px' : '0'
    }"
  >
    <div
      v-if="!query.isInContext"
      class="integration-header text-center text-white"
      :style="{ width: cardItemWidth ? cardItemWidth + 'px' : undefined }"
    >
      Integration with other bounded contexts
    </div>
    <div
      :id="`query-${system.index}-${event.index}-${queryIndex}`"
      ref="cardItemRef"
      :data-card-id="query.id"
      class="card-item hover-container height-transition-hover-container"
      :class="{ 'card-item--integration': !query.isInContext }"
      :style="{
        'border-color': effectiveBorderColor,
        '--transitionContainerHeight': !query.schemaData.hasSchema || !showEntities ? '24px' : '16px'
      }"
    >
      <beam-card
        v-if="!query.schemaData.hasSchema"
        :id="query.id"
        :event="event"
        :card="query"
        @selectedSchemaType="onSelectedSchemaType"
      />
      <div v-else>
        <domain-model-query-inputs
          v-model:editing="editing"
          :event="event"
          :query="query"
          :queryIndex="queryIndex"
          @updateTextareaHeight="updateTextareaHeight"
          @getTextareaRef="getTextAreaRef"
        />
        <domain-model-read-model-fields
          v-if="showEntities && !!(query.schemaData?.fields ?? [])?.length"
          class="py-2 fields"
          :fields="query.schemaData?.fields ?? []"
          :depth="1"
          :maxDepth="3"
          :path="[]"
          :event="event"
          :cardId="query.id"
          :schemaId="query.schemaId"
          :disabled="store.state.isWorkflowDisabled"
          :rootEntityId="query.schemaData?.schema?.entityId"
          :checkRootFieldIssue="checkRootFieldIssue"
          :checkEntityMismatch="checkEntityMismatch"
          :getEntityFieldRelatedEntity="getEntityFieldRelatedEntityForField"
          :fixEntityMismatch="fixEntityMismatchForField"
        />
        <div v-if="linkedEntity" class="d-flex flex-column align-end py-1">
          <workflow-aggregate-select-bounded-context :entity="linkedEntity" />
        </div>
      </div>
      <domain-model-card-toolbar
        v-if="!store.state.isWorkflowDisabled"
        :showAi="!query.schemaData.hasSchema"
        :showFilters="true"
        :event="event"
        :card="query"
        :menuPositioningActivator="cardElement"
      />
    </div>
    <div
      v-if="!store.state.isWorkflowDisabled && queryIndex === system.queries[event.index].length - 1"
      class="add-more-query-btn"
    >
      <v-tooltip location="bottom" open-delay="100">
        <template v-slot:activator="{ on, props }">
          <v-btn
            v-bind="props"
            v-on="{ ...on }"
            size="small"
            tabindex="-1"
            icon="mdi-plus"
            @click="createCard(event.id, queryCardType.id, `<p>${queryCardType.title} for ${event.description}</p>`)"
          />
        </template>
        <span>Add {{ queryCardType?.title }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import BeamCard from '@/components/BeamCard'
import DomainModelCardToolbar from '@/components/DomainModelCardToolbar'
import DomainModelQueryInputs from '@/components/DomainModelQueryInputs.vue'
import DomainModelReadModelFields from '@/components/DomainModelReadModelFields.vue'
import WorkflowAggregateSelectBoundedContext from '@/components/WorkflowAggregateSelectBoundedContext.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { useSortFields } from '@/composables/useSortFields'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { SchemaType } from '@/constants/schema'

const {
  query,
  queryIndex,
  system,
  event,
  showEntities
} = defineProps({
  query: Object,
  queryIndex: Number,
  system: Object,
  event: Object,
  showEntities: Boolean
})

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))
const linkedEntity = computed(() => {
  const entityId = query.schemaData?.schema?.entityId
  if (!entityId) {
    return null
  }
  return workflow.value.schemas?.find((s) => s.id === entityId) || null
})

const {
  cardTypeColor,
  hasFieldIssue,
  getMatchingFieldInEntity,
  getReadModelCardHeight,
  createCard
} = useDomainModel()
const { initSortable, destroySortable } = useSortFields()

const cardElement = ref(null)
const cardItemRef = ref(null)
const cardItemWidth = ref(0)
const textareaRef = ref(null)
const textareaHeight = ref(0)
const selectedSchemaType = ref(null)
const editing = ref(false)
let cardResizeObserver = null

const domainEventCardType = computed(() => diagramHelpers.methods.getDomainEventCardType(cardTypes.value))
const cardHeight = computed(() => getReadModelCardHeight(event, query, textareaHeight.value ?? 0))

const linkedSchemaIsDomainEvent = computed(() => {
  if (selectedSchemaType.value === SchemaType.DOMAIN_EVENT) return true
  if (!query.schemaId) return false
  const schema = (workflow.value?.schemas ?? []).find(s => s.id === query.schemaId)
  return schema?.type === SchemaType.DOMAIN_EVENT
})

const effectiveBorderColor = computed(() => {
  if (linkedSchemaIsDomainEvent.value && domainEventCardType.value) {
    return cardTypeColor(domainEventCardType.value)
  }
  return cardTypeColor(queryCardType.value)
})

const onSelectedSchemaType = (type) => {
  selectedSchemaType.value = type
}

const getTextAreaRef = (element) => {
  textareaRef.value = element || null
  updateTextareaHeight()
}

const updateTextareaHeight = async () => {
  await nextTick()
  if (textareaRef.value) {
    textareaHeight.value = textareaRef.value.offsetHeight
  }
}

const getQueryRootEntity = (entityId) => {
  return (workflow.value.schemas || [])?.find((entity) => entity.id === entityId)
}

const isSelectedEntityMismatch = (query, field) => {
  const rootEntity = getQueryRootEntity(query.schemaData.schema?.entityId)

  if (!rootEntity) {
    return false
  }

  const matchingFieldInEntity = getMatchingFieldInEntity(rootEntity, field.name)

  if (
    !matchingFieldInEntity ||
    (!field.relatedEntityId && !matchingFieldInEntity?.relatedEntityId) ||
    field.relatedEntityId === matchingFieldInEntity?.relatedEntityId
  ) {
    return false
  }

  // Checking for actual entity existence
  const readModelFieldRelatedEntity = field.relatedEntityId
    ? workflow.value.schemas?.find((entity) => entity.id === field.relatedEntityId)
    : true
  const entityFieldRelatedEntity = matchingFieldInEntity?.relatedEntityId
    ? workflow.value.schemas?.find((entity) => entity.id === matchingFieldInEntity?.relatedEntityId)
    : true

  return !!readModelFieldRelatedEntity && !!entityFieldRelatedEntity
}

const getEntityFieldRelatedEntity = (query, field) => {
  const rootEntity = getQueryRootEntity(query.schemaData.schema?.entityId)
  if (!rootEntity) {
    return null
  }

  const matchingFieldInEntity = getMatchingFieldInEntity(rootEntity, field.name)
  if (!matchingFieldInEntity?.relatedEntityId) {
    return null
  }

  return workflow.value.schemas?.find((entity) => entity.id === matchingFieldInEntity.relatedEntityId)
}

const fixEntityReferenceMismatch = async (query, field, fieldIndex) => {
  const rootEntity = getQueryRootEntity(query.schemaData.schema?.entityId)
  if (!rootEntity) {
    return
  }

  const matchingFieldInEntity = getMatchingFieldInEntity(rootEntity, field.name)
  if (!matchingFieldInEntity) {
    return
  }

  const schemas = safeStructuredClone(workflow.value.schemas || [])
  const schemaIndex = schemas.findIndex((s) => s.id === query.schemaId)
  if (schemaIndex === -1) {
    return
  }

  if (!schemas[schemaIndex].fields || fieldIndex < 0 || fieldIndex >= schemas[schemaIndex].fields.length) {
    return
  }

  schemas[schemaIndex].fields[fieldIndex].relatedEntityId = matchingFieldInEntity.relatedEntityId || null

  await workflowApi.updateSchemas({
    schemas,
    workflow: workflow.value
  })
}

const checkRootFieldIssue = (field) => hasFieldIssue(query.schemaData?.schema?.entityId, field)

const checkEntityMismatch = (field) => isSelectedEntityMismatch(query, field)

const getEntityFieldRelatedEntityForField = (field) => getEntityFieldRelatedEntity(query, field)

const fixEntityMismatchForField = (field, fieldIndex) => fixEntityReferenceMismatch(query, field, fieldIndex)

watch(() => query, () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      initSortable()
    })
  })
})

onMounted(() => {
  initSortable()
  if (cardItemRef.value) {
    cardItemWidth.value = cardItemRef.value.offsetWidth
    cardResizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          cardItemWidth.value = entry.target.offsetWidth
        }
      })
    })
    cardResizeObserver.observe(cardItemRef.value)
  }
})

onBeforeUnmount(() => {
  destroySortable()
  if (cardResizeObserver) {
    cardResizeObserver.disconnect()
  }
})
</script>

<style scoped>
.v-menu > .v-overlay__content > .v-list {
  padding: 4px 0 !important;
}

:deep(.v-textarea .v-field__input) {
  font-size: 12px;
  padding: 8px;
}

.add-more-query-btn {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  transition: all 0.2s;
  opacity: 0;
}

.integration-header {
  font-size: 10px;
  padding: 6px 4px;
  background-color: #363A40;
  border: 1px solid #363A40;
  border-top-width: 2px;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  margin: 0 auto;
  box-sizing: border-box;
  line-height: 1;
}

.card-item--integration {
  border-top: none !important;
  border-radius: 0 0 8px 8px !important;
}

</style>
